// src/components/LoadingState.js

import React from 'react';
import {
Box,
Typography,
LinearProgress,
useTheme,
Avatar,
Fade,
Zoom
} from '@mui/material';
import {
LocalHospital,
MedicalServices,
Healing,
MonitorHeart,
Biotech,
Medication,
Sanitizer
} from '@mui/icons-material';

const LoadingState = ({ 
message = "Loading...", 
icon = null,
fullPage = false,
size = "medium",
variant = "default"
}) => {
const theme = useTheme();

// Size mappings
const sizeMap = {
    small: {
        iconSize: 40,
        fontSize: 'h6',
        progressHeight: 4,
        containerHeight: fullPage ? '100vh' : '200px'
    },
    medium: {
        iconSize: 60,
        fontSize: 'h5',
        progressHeight: 6,
        containerHeight: fullPage ? '100vh' : '300px'
    },
    large: {
        iconSize: 80,
        fontSize: 'h4',
        progressHeight: 8,
        containerHeight: fullPage ? '100vh' : '400px'
    }
};

const sizes = sizeMap[size];

// Medical icons for different variants
const getIcon = () => {
    if (icon) return icon;
    
    switch(variant) {
        case 'medical':
            return <LocalHospital sx={{ fontSize: sizes.iconSize }} />;
        case 'healing':
            return <Healing sx={{ fontSize: sizes.iconSize }} />;
        case 'monitor':
            return <MonitorHeart sx={{ fontSize: sizes.iconSize }} />;
        case 'lab':
            return <Biotech sx={{ fontSize: sizes.iconSize }} />;
        case 'pharmacy':
            return <Medication sx={{ fontSize: sizes.iconSize }} />;
        case 'sanitizer':
            return <Sanitizer sx={{ fontSize: sizes.iconSize }} />;
        default:
            return <MedicalServices sx={{ fontSize: sizes.iconSize }} />;
    }
};

return (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: sizes.containerHeight,
            width: '100%',
            gap: 3,
            p: 4
        }}
    >
        <Zoom in={true} style={{ transitionDelay: '200ms' }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                {/* Animated medical icon */}
                <Box
                    sx={{
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                            '0%': { transform: 'scale(1)', opacity: 0.8 },
                            '50%': { transform: 'scale(1.1)', opacity: 1 },
                            '100%': { transform: 'scale(1)', opacity: 0.8 },
                        },
                    }}
                >
                    <Avatar
                        sx={{
                            width: sizes.iconSize + 20,
                            height: sizes.iconSize + 20,
                            bgcolor: theme.palette.primary.light,
                            color: theme.palette.primary.main,
                            boxShadow: `0 4px 15px ${theme.palette.primary.main}40`
                        }}
                    >
                        {getIcon()}
                    </Avatar>
                </Box>
                
                {/* Animated rings */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -10,
                        left: -10,
                        right: -10,
                        bottom: -10,
                        borderRadius: '50%',
                        border: `2px solid ${theme.palette.primary.light}`,
                        animation: 'ripple 1.5s ease-out infinite',
                        '@keyframes ripple': {
                            '0%': { transform: 'scale(0.8)', opacity: 0.5 },
                            '100%': { transform: 'scale(1.3)', opacity: 0 },
                        },
                    }}
                />
            </Box>
        </Zoom>
        
        <Fade in={true} timeout={1000}>
            <Box sx={{ textAlign: 'center', maxWidth: 400, width: '100%' }}>
                <Typography 
                    variant={sizes.fontSize} 
                    sx={{ 
                        color: theme.palette.text.secondary,
                        mb: 2,
                        fontWeight: 500,
                        letterSpacing: '0.5px'
                    }}
                >
                    {message}
                </Typography>
                
                <LinearProgress 
                    sx={{ 
                        height: sizes.progressHeight, 
                        borderRadius: 4,
                        backgroundColor: theme.palette.primary.light + '40',
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.info.main})`,
                        }
                    }} 
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                    {[...Array(3)].map((_, i) => (
                        <Box
                            key={i}
                            sx={{
                                width: size === 'small' ? 6 : 8,
                                height: size === 'small' ? 6 : 8,
                                borderRadius: '50%',
                                backgroundColor: theme.palette.primary.main,
                                animation: `bounce 1.4s ease-in-out ${i * 0.2}s infinite`,
                                '@keyframes bounce': {
                                    '0%, 100%': { transform: 'translateY(0)', opacity: 0.6 },
                                    '50%': { transform: 'translateY(-10px)', opacity: 1 },
                                },
                            }}
                        />
                    ))}
                </Box>
            </Box>
        </Fade>
    </Box>
);
};

export default LoadingState;